import { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, AlertTriangle, CheckCircle2, PlayCircle, ShieldAlert } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { toast } from 'sonner';

/**
 * One-time admin data cleanup page.
 *
 * Hidden URL: /admin/data-cleanup
 * Backend: GET /api/admin/data-cleanup?confirm=YES_CLEAN_DATA (dry-run)
 *          POST /api/admin/data-cleanup?confirm=YES_CLEAN_DATA (apply)
 *
 * Guardrails:
 *  - Backend enforces master-admin-only check via MASTER_ADMIN_EMAIL
 *  - Requires the confirm query param to match server-side constant
 *  - Remove this page + the backend file after cleanup is done
 */
export default function AdminDataCleanup() {
  const api = useApi();
  const [dryRun, setDryRun] = useState(null);
  const [applied, setApplied] = useState(null);
  const [loadingDry, setLoadingDry] = useState(false);
  const [loadingApply, setLoadingApply] = useState(false);
  const [wipeCount, setWipeCount] = useState(null);
  const [wipeResult, setWipeResult] = useState(null);
  const [loadingWipe, setLoadingWipe] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);

  async function runDryRun() {
    setLoadingDry(true);
    setApplied(null);
    try {
      const data = await api.get('/admin/data-cleanup?confirm=YES_CLEAN_DATA');
      setDryRun(data);
      toast.success('Dry-run complete — review counts below.');
    } catch (err) {
      toast.error(err.message || 'Dry-run failed');
    } finally {
      setLoadingDry(false);
    }
  }

  async function runApply() {
    if (!window.confirm('This will permanently delete TEST_* records and modify your seller profile. Continue?')) return;
    setLoadingApply(true);
    try {
      const data = await api.post('/admin/data-cleanup?confirm=YES_CLEAN_DATA', {});
      setApplied(data);
      toast.success('Cleanup applied successfully.');
    } catch (err) {
      toast.error(err.message || 'Cleanup failed');
    } finally {
      setLoadingApply(false);
    }
  }

  async function previewWipe() {
    setLoadingWipe(true);
    try {
      const data = await api.get('/admin/wipe-signal-drafts?confirm=YES_CLEAN_DATA');
      setWipeCount(data.would_delete);
      setWipeResult(null);
      toast.info(`${data.would_delete} stale draft signal${data.would_delete === 1 ? '' : 's'} found`);
    } catch (err) {
      toast.error(err.message || 'Preview failed');
    } finally {
      setLoadingWipe(false);
    }
  }

  async function applyWipe() {
    if (!window.confirm(`Permanently delete ${wipeCount} draft signal(s)? This cannot be undone.`)) return;
    setLoadingWipe(true);
    try {
      const data = await api.post('/admin/wipe-signal-drafts?confirm=YES_CLEAN_DATA', {});
      setWipeResult(data);
      setWipeCount(0);
      toast.success(data.message || 'Drafts wiped');
    } catch (err) {
      toast.error(err.message || 'Wipe failed');
    } finally {
      setLoadingWipe(false);
    }
  }

  async function loadDiagnostics() {
    setLoadingDiag(true);
    try {
      const data = await api.get('/admin/sweep-diagnostics?confirm=YES_CLEAN_DATA');
      setDiagnostics(data);
      toast.success('Diagnostics loaded');
    } catch (err) {
      toast.error(err.message || 'Diagnostics failed');
    } finally {
      setLoadingDiag(false);
    }
  }

  function renderActionList(payload) {
    if (!payload?.actions) return null;
    return (
      <div className="space-y-3 mt-4">
        {payload.actions.map((a, i) => (
          <div
            key={i}
            data-testid={`cleanup-action-${a.fix}`}
            className="p-3 border rounded-sm bg-white text-sm"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100">
                Fix {a.fix}
              </span>
              <span className="font-medium">{a.name}</span>
              {a.skipped && <span className="text-xs text-gray-500 ml-auto">SKIPPED</span>}
              {a.applied === true && <span className="text-xs text-green-600 ml-auto">APPLIED</span>}
              {a.applied === false && <span className="text-xs text-amber-600 ml-auto">DRY-RUN</span>}
            </div>
            {a.skipped && <p className="text-xs text-gray-500">{a.skipped}</p>}
            {a.would_modify !== undefined && (
              <p className="text-xs">Would modify <strong>{a.would_modify}</strong> records</p>
            )}
            {a.modified !== undefined && (
              <p className="text-xs">Modified <strong>{a.modified}</strong> records</p>
            )}
            {a.cities && (
              <p className="text-xs">Cities: <strong>{a.cities.join(', ')}</strong></p>
            )}
            {a.details && (
              <ul className="text-xs space-y-0.5 mt-1">
                {a.details.map((d, idx) => (
                  <li key={idx} className="flex justify-between">
                    <span>{d.collection}</span>
                    <span className="font-mono">
                      {d.skipped
                        ? 'none'
                        : d.deleted !== undefined
                          ? `deleted ${d.deleted}`
                          : `would delete ${d.would_delete}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {a.scrubbed_fields && (
              <ul className="text-xs space-y-0.5 mt-1">
                {a.scrubbed_fields.map((f, idx) => (
                  <li key={idx}>
                    <span className="font-mono">{f.field}</span>: <span className="text-red-600">{f.old}</span>
                  </li>
                ))}
              </ul>
            )}
            {a.old !== undefined && a.new !== undefined && (
              <p className="text-xs">
                <span className="font-mono">{String(a.old)}</span> → <span className="font-mono">{String(a.new)}</span>
              </p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Admin Data Cleanup
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              One-time scrub of TEST_* pollution + geographic filter setup. Master admin only.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                data-testid="cleanup-dry-run-btn"
                onClick={runDryRun}
                disabled={loadingDry || loadingApply}
                variant="outline"
                className="flex-1"
              >
                {loadingDry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Run Dry-Run (read-only)
              </Button>
              <Button
                data-testid="cleanup-apply-btn"
                onClick={runApply}
                disabled={loadingApply || !dryRun}
                className="flex-1 bg-amber-700 hover:bg-amber-800 text-white"
              >
                {loadingApply ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Apply Cleanup
              </Button>
            </div>

            {!dryRun && (
              <div className="flex items-start gap-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 p-3 rounded-sm">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  Click <strong>Dry-Run</strong> first. The Apply button activates after a successful dry-run.
                  Apply will delete TEST_* records and modify your seller profile.
                </div>
              </div>
            )}

            {dryRun && (
              <div data-testid="cleanup-dry-run-output">
                <h3 className="text-sm font-semibold text-gray-700">Dry-Run Results</h3>
                {renderActionList(dryRun)}
              </div>
            )}

            {applied && (
              <div
                data-testid="cleanup-apply-output"
                className="border-t pt-4"
              >
                <h3 className="text-sm font-semibold text-green-700 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Cleanup Applied
                </h3>
                {renderActionList(applied)}
              </div>
            )}

            {/* ───── Wipe Signal Drafts ───── */}
            <div className="border-t pt-4 mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Stale Signal Drafts</h3>
              <p className="text-xs text-gray-600 mb-3">
                Drafts generated by the older filter logic before this deploy. Wipe them so the next
                &ldquo;Generate Now&rdquo; produces a clean batch from the new code.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  data-testid="wipe-drafts-preview-btn"
                  onClick={previewWipe}
                  disabled={loadingWipe}
                  variant="outline"
                  className="flex-1"
                >
                  {loadingWipe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                  Count Stale Drafts
                </Button>
                <Button
                  data-testid="wipe-drafts-apply-btn"
                  onClick={applyWipe}
                  disabled={loadingWipe || wipeCount === null || wipeCount === 0}
                  className="flex-1 bg-red-700 hover:bg-red-800 text-white"
                >
                  {loadingWipe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Wipe {wipeCount !== null ? `(${wipeCount})` : 'All'}
                </Button>
              </div>
              {wipeCount !== null && !wipeResult && (
                <p className="text-xs text-gray-600 mt-2" data-testid="wipe-drafts-count">
                  Found <strong>{wipeCount}</strong> stale draft{wipeCount === 1 ? '' : 's'} for your account.
                </p>
              )}
              {wipeResult && (
                <p className="text-xs text-green-700 mt-2 flex items-center gap-1" data-testid="wipe-drafts-result">
                  <CheckCircle2 className="w-3 h-3" />
                  Wiped <strong>{wipeResult.deleted}</strong> draft{wipeResult.deleted === 1 ? '' : 's'}.
                </p>
              )}
            </div>

            {/* ───── Sweep Diagnostics ───── */}
            <div className="border-t pt-4 mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Sweep Diagnostics</h3>
              <p className="text-xs text-gray-600 mb-3">
                Why did Generate Now produce 0 signals? See what candidates were found, how many
                were rejected, and the top rejection reasons across your last 5 sweep runs.
              </p>
              <Button
                data-testid="diagnostics-load-btn"
                onClick={loadDiagnostics}
                disabled={loadingDiag}
                variant="outline"
                className="w-full"
              >
                {loadingDiag ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Load Sweep Diagnostics
              </Button>

              {diagnostics && (
                <div className="mt-4 space-y-3 text-sm" data-testid="diagnostics-output">
                  {/* Current state */}
                  <div className="p-3 border rounded-sm bg-white">
                    <div className="text-xs font-semibold text-gray-600 mb-2">CURRENT STATE</div>
                    <div className="flex justify-between"><span>Drafts awaiting review</span><strong>{diagnostics.current_state?.drafts_awaiting_review ?? 0}</strong></div>
                    <div className="flex justify-between"><span>Live active signals</span><strong>{diagnostics.current_state?.live_active_signals ?? 0}</strong></div>
                  </div>

                  {/* Profile summary */}
                  <div className="p-3 border rounded-sm bg-white">
                    <div className="text-xs font-semibold text-gray-600 mb-2">SELLER PROFILE SUMMARY</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span>Industry</span><span className="font-mono text-gray-700">{diagnostics.profile_summary?.seller_industry}</span></div>
                      <div className="flex justify-between"><span>Value prop</span><span className="font-mono text-gray-700 text-right max-w-[60%] truncate">{diagnostics.profile_summary?.value_proposition}</span></div>
                      <div className="flex justify-between"><span>Target segments</span><strong>{diagnostics.profile_summary?.target_segments_count}</strong></div>
                      <div className="flex justify-between"><span>Products</span><strong>{diagnostics.profile_summary?.products_count}</strong></div>
                      <div className="flex justify-between"><span>Product tiers</span><strong>{diagnostics.profile_summary?.product_tiers_count}</strong></div>
                      <div className="flex justify-between">
                        <span>Geo filter</span>
                        <strong className={diagnostics.profile_summary?.geo_filter_active ? 'text-green-700' : 'text-amber-700'}>
                          {diagnostics.profile_summary?.geo_filter_active ? 'ACTIVE' : 'INACTIVE'}
                        </strong>
                      </div>
                      <div className="flex justify-between">
                        <span>Cities (Market Coverage UI)</span>
                        <strong>{diagnostics.profile_summary?.market_coverage_cities_count}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>Cities (Seller Profile / used by filter)</span>
                        <strong className={(diagnostics.profile_summary?.seller_profile_cities_count ?? 0) > 0 ? 'text-green-700' : 'text-red-700'}>
                          {diagnostics.profile_summary?.seller_profile_cities_count}
                        </strong>
                      </div>
                      {diagnostics.profile_summary?.market_coverage_cities_count > 0 && diagnostics.profile_summary?.seller_profile_cities_count === 0 && (
                        <div className="p-2 mt-1 rounded bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                          ⚠ You have cities in Market Coverage but they haven&apos;t been copied into the seller profile.
                          Run the cleanup above to activate the geo filter.
                        </div>
                      )}
                      <div className="flex justify-between"><span>Detection mode</span><span className="font-mono">{diagnostics.profile_summary?.detection_mode}</span></div>
                    </div>
                  </div>

                  {/* Recent runs */}
                  {(diagnostics.recent_runs || []).map((r, i) => (
                    <div key={i} className="p-3 border rounded-sm bg-white" data-testid={`diagnostics-run-${i}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-gray-600">RUN — {new Date(r.started_at).toLocaleString()}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${r.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <div className="flex justify-between"><span>Candidates seen</span><strong>{r.candidates_seen ?? '—'}</strong></div>
                        <div className="flex justify-between"><span>Signals created</span><strong className="text-green-700">{r.signals_created ?? 0}</strong></div>
                        <div className="flex justify-between"><span>Rejected (quality)</span><strong className="text-red-700">{r.rejected_quality ?? 0}</strong></div>
                        <div className="flex justify-between"><span>Signals updated</span><strong>{r.signals_updated ?? 0}</strong></div>
                        <div className="flex justify-between"><span>Duration</span><strong>{r.duration_seconds ? `${Math.round(r.duration_seconds)}s` : '—'}</strong></div>
                        <div className="flex justify-between"><span>Sources scanned</span><strong>{(r.sources_scanned || []).length}</strong></div>
                      </div>
                      {r.rejection_top_reasons && Object.keys(r.rejection_top_reasons).length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <div className="text-xs font-semibold text-gray-600 mb-1">TOP REJECTION REASONS</div>
                          <ul className="text-xs space-y-0.5">
                            {Object.entries(r.rejection_top_reasons)
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 5)
                              .map(([reason, count], idx) => (
                                <li key={idx} className="flex justify-between gap-2">
                                  <span className="truncate text-gray-700">{reason}</span>
                                  <strong className="flex-shrink-0">×{count}</strong>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                      {(r.errors || []).length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <div className="text-xs font-semibold text-red-600 mb-1">ERRORS ({(r.errors || []).length})</div>
                          <ul className="text-xs space-y-0.5 text-red-700">
                            {(r.errors || []).slice(0, 3).map((e, idx) => (
                              <li key={idx} className="truncate">{e}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}

                  {(!diagnostics.recent_runs || diagnostics.recent_runs.length === 0) && (
                    <p className="text-xs text-gray-500 italic p-3">No sweep runs found. Click &ldquo;Generate Now&rdquo; on the Market Signals page first.</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
